// Builds a single CSV with three sections — Batting, Pitching, Fielding — for one player and
// triggers a browser download. Each section has the player's season total followed by a row per
// game. Column sets mirror the "standard" stat tables (see stats-columns/baseball/*-standard.ts):
// batting reads from `offense`, pitching and fielding from the shared `defense` blob.
import * as Eden from "src/eden/content";
import * as Team from "src/state/modules/team";
import * as DateUtils from "src/utils/date-utils";
import {
  fourDigitNumberFormatter,
  inningsFormatter,
  percentFormatter,
} from "src/components/pages/Teams/stats-columns/formatters";

export type PlayerStatGame = {
  date: string;
  opponent: string;
  stats: Eden.StatData;
};

type StatColumn = {
  header: string;
  value: (stats: Eden.StatData) => string;
};

const num = (v: number | undefined | null): string => (v === undefined || v === null ? "" : String(v));
const rate4 = (v: number | undefined): string => fourDigitNumberFormatter({ value: v });
const pct = (v: number | undefined): string => percentFormatter({ value: v });
const ip = (v: number | undefined): string => inningsFormatter({ value: v });

const battingColumns: StatColumn[] = [
  { header: "GP", value: (s) => num(s.offense?.gp) },
  { header: "PA", value: (s) => num(s.offense?.pa) },
  { header: "AB", value: (s) => num(s.offense?.ab) },
  { header: "AVG", value: (s) => rate4(s.offense?.avg) },
  { header: "OBP", value: (s) => rate4(s.offense?.obp) },
  { header: "OPS", value: (s) => rate4(s.offense?.ops) },
  { header: "SLG", value: (s) => rate4(s.offense?.slg) },
  { header: "H", value: (s) => num(s.offense?.h) },
  { header: "1B", value: (s) => num(s.offense?.["1B"]) },
  { header: "2B", value: (s) => num(s.offense?.["2B"]) },
  { header: "3B", value: (s) => num(s.offense?.["3B"]) },
  { header: "HR", value: (s) => num(s.offense?.hr) },
  { header: "RBI", value: (s) => num(s.offense?.rbi) },
  { header: "R", value: (s) => num(s.offense?.r) },
  { header: "BB", value: (s) => num(s.offense?.bb) },
  { header: "SO", value: (s) => num(s.offense?.so) },
  { header: "K-L", value: (s) => num(s.offense?.sol) },
  { header: "HBP", value: (s) => num(s.offense?.hbp) },
  { header: "SAC", value: (s) => num(s.offense?.shb) },
  { header: "SF", value: (s) => num(s.offense?.shf) },
  { header: "ROE", value: (s) => num(s.offense?.roe) },
  { header: "FC", value: (s) => num(s.offense?.fc) },
  { header: "SB", value: (s) => num(s.offense?.sb) },
  { header: "SB%", value: (s) => pct(s.offense?.["SB%"]) },
  { header: "CS", value: (s) => num(s.offense?.cs) },
  { header: "PIK", value: (s) => num(s.offense?.pik) },
];

const pitchingColumns: StatColumn[] = [
  { header: "IP", value: (s) => ip(s.defense?.ip) },
  { header: "GP", value: (s) => num(s.defense?.["GP:P"]) },
  { header: "GS", value: (s) => num(s.defense?.gs) },
  { header: "BF", value: (s) => num(s.defense?.bf) },
  { header: "#P", value: (s) => num(s.defense?.["#P"]) },
  { header: "W", value: (s) => num(s.defense?.w) },
  { header: "L", value: (s) => num(s.defense?.l) },
  { header: "SV", value: (s) => num(s.defense?.sv) },
  { header: "SVO", value: (s) => num(s.defense?.svo) },
  { header: "BS", value: (s) => num(s.defense?.bs) },
  { header: "SV%", value: (s) => pct(s.defense?.["SV%"]) },
  { header: "H", value: (s) => num(s.defense?.h) },
  { header: "R", value: (s) => num(s.defense?.r) },
  { header: "ER", value: (s) => num(s.defense?.er) },
  { header: "BB", value: (s) => num(s.defense?.bb) },
  { header: "SO", value: (s) => num(s.defense?.so) },
  { header: "K-L", value: (s) => num(s.defense?.sol) },
  { header: "HBP", value: (s) => num(s.defense?.hbp) },
  { header: "ERA", value: (s) => rate4(s.defense?.era) },
  { header: "WHIP", value: (s) => rate4(s.defense?.whip) },
  { header: "LOB", value: (s) => num(s.defense?.lob) },
  { header: "BK", value: (s) => num(s.defense?.bk) },
  { header: "PIK", value: (s) => num(s.defense?.pik) },
  { header: "CS", value: (s) => num(s.defense?.cs) },
  { header: "SB", value: (s) => num(s.defense?.sb) },
  { header: "WP", value: (s) => num(s.defense?.wp) },
  { header: "BAA", value: (s) => rate4(s.defense?.baa) },
];

const fieldingColumns: StatColumn[] = [
  { header: "TC", value: (s) => num(s.defense?.tc) },
  { header: "A", value: (s) => num(s.defense?.a) },
  { header: "PO", value: (s) => num(s.defense?.po) },
  { header: "FPCT", value: (s) => rate4(s.defense?.fpct) },
  { header: "E", value: (s) => num(s.defense?.e) },
  { header: "DP", value: (s) => num(s.defense?.dp) },
];

const hasBatting = (s: Eden.StatData): boolean => !!s.offense && (s.offense.pa ?? 0) > 0;
const hasPitching = (s: Eden.StatData): boolean =>
  !!s.defense && ((s.defense.bf ?? 0) > 0 || (s.defense.ip ?? 0) > 0 || (s.defense["#P"] ?? 0) > 0);
const hasFielding = (s: Eden.StatData): boolean =>
  !!s.defense &&
  ((s.defense.tc ?? 0) > 0 || (s.defense.po ?? 0) > 0 || (s.defense.a ?? 0) > 0 || (s.defense.e ?? 0) > 0 ||
    (s.defense.INN ?? 0) > 0);

const formatGameDate = (date: string): string =>
  date ? DateUtils.formatDate(date, undefined, DateUtils.DATE_FORMAT.monthDayAndYear) : "";

const buildSection = (
  title: string,
  columns: StatColumn[],
  seasonStats: Eden.StatData | undefined,
  games: PlayerStatGame[],
  hasActivity: (s: Eden.StatData) => boolean
): string[][] => {
  const rows: string[][] = [[title]];
  rows.push(["Date", "Opponent", ...columns.map((c) => c.header)]);

  let any = false;
  if (seasonStats && hasActivity(seasonStats)) {
    rows.push(["Season Total", "", ...columns.map((c) => c.value(seasonStats))]);
    any = true;
  }
  games
    .filter((g) => hasActivity(g.stats))
    .forEach((g) => {
      any = true;
      rows.push([formatGameDate(g.date), g.opponent, ...columns.map((c) => c.value(g.stats))]);
    });

  if (!any) {
    rows.push([`No ${title.toLowerCase()} stats recorded`]);
  }
  return rows;
};

const escapeCsv = (field: string): string =>
  /[",\n\r]/.test(field) ? `"${field.replace(/"/g, '""')}"` : field;

const toCsv = (rows: string[][]): string =>
  rows.map((row) => row.map((cell) => escapeCsv(cell ?? "")).join(",")).join("\r\n");

const downloadCsv = (filename: string, csv: string): void => {
  // BOM so Excel detects UTF-8.
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const sanitizeForFilename = (s: string): string =>
  s.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "player";

export const exportPlayerStatsCsv = (args: {
  player: Team.Player;
  team?: Team.Team;
  seasonStats?: Eden.StatData;
  games: PlayerStatGame[];
}): void => {
  const { player, team, seasonStats, games } = args;
  const name = `${player.first_name ?? ""} ${player.last_name ?? ""}`.trim();
  const jersey = player.number ? `#${player.number}` : "";

  const rows: string[][] = [];
  rows.push(["Player", [name, jersey].filter(Boolean).join(" ")]);
  if (team?.name) rows.push(["Team", team.name]);
  rows.push(["Bats", player.batting_side || "-", "Throws", player.throwing_hand || "-"]);
  rows.push(["Exported", DateUtils.formatDate(new Date(), undefined, DateUtils.DATE_FORMAT.monthDayAndYear)]);
  rows.push([]);

  // Sort games chronologically for the per-game rows.
  const orderedGames = [...games].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  rows.push(...buildSection("Batting", battingColumns, seasonStats, orderedGames, hasBatting));
  rows.push([]);
  rows.push(...buildSection("Pitching", pitchingColumns, seasonStats, orderedGames, hasPitching));
  rows.push([]);
  rows.push(...buildSection("Fielding", fieldingColumns, seasonStats, orderedGames, hasFielding));

  const seasonSuffix = team?.seasonYear ? `_${team.seasonYear}` : "";
  const filename = `${sanitizeForFilename(name)}_stats${seasonSuffix}.csv`;
  downloadCsv(filename, toCsv(rows));
};
